import React, { useEffect, useState } from 'react';
import { Button, Form, Spinner, Alert, Modal, ListGroup, InputGroup, Breadcrumb } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { cookie } from 'utils/cookies';

import {
    FETCH_STATION_WISE_FORMS,
    DOWNLOAD_CHECKLIST_FORM,
    STATUS_CHANGE,
    CREATE_CHECKLIST,
    DELETE_CHECKLIST_FORM,
    ASSIGN_TO_CONTRACTOR,
    ASSIGN_TO_MANAGER,
} from 'actions/formActions';
import { 
    IconDownload, 
    IconEdit, 
    IconLoader2, 
    IconCheck, 
    IconX, 
    IconTrash, 
    IconUserPlus, 
    IconShieldCheck, 
    IconInfoCircle, 
    IconSearch,
    IconPlus,
    IconFilter,
    IconCalendar,
    IconBuildingStore,
    IconSubtask,
    IconLayoutGrid,
    IconArrowLeft,
    IconBox
} from '@tabler/icons-react';
import { FETCH_USERS } from 'actions/userActions';


const StationForms = () => {
    const { stationId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
        loading,
        stationwiseformslist = [],
        downloadLoading,
        error,
        statusLoading,
        deleteLoading,
    } = useSelector((state) => state.checklist);

    const { users } = useSelector((state) => state.users);

    const contractors =
        Array.isArray(users)
            ? users.filter((u) => (u?.role || '').toLowerCase() === 'contractor')
            : [];

    const managers =
        Array.isArray(users)
            ? users.filter((u) => (u?.role || '').toLowerCase() === 'manager')
            : [];





    const [search, setSearch] = useState('');
    const [formFilter, setFormFilter] = useState('');
    const [downloadInProgress, setDownloadInProgress] = useState(null);
    const [statusChangeInProgress, setStatusChangeInProgress] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [userFilter, setUserFilter] = useState('');
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [selectedFormId, setSelectedFormId] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [formName, setFormName] = useState('');
    const [showAddFormsModal, setShowAddFormsModal] = useState(false);
    const [quantities, setQuantities] = useState({});
    const [addFormError, setAddFormError] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteInProgress, setDeleteInProgress] = useState(null);

    // ✅ Contractor modal state (RESTORING)
    const [showContractorModal, setShowContractorModal] = useState(false);
    const [contractorLevel, setContractorLevel] = useState(null);
    const [selectedContractorId, setSelectedContractorId] = useState('');
    const [assignInProgress, setAssignInProgress] = useState(false);
    const [showManagerModal, setShowManagerModal] = useState(false);
    const [managerLevel, setManagerLevel] = useState(null);
    const [selectedManagerId, setSelectedManagerId] = useState('');
    const [managerAssignInProgress, setManagerAssignInProgress] = useState(false);


    const [isSearchExpanded, setIsSearchExpanded] = useState(false);

    const clearFilters = () => {
        setSearch('');
        setFormFilter('');
        setStatusFilter('');
        setUserFilter('');
        setStartDate('');
        setEndDate('');
    };

    // RESTORED contractor functions
    const openContractorModal = (form, level /* 'l1' | 'l2' | 'root' */) => {
        setSelectedFormId(form._id);
        setFormName(form.formName);
        setContractorLevel(level);
        setSelectedContractorId('');
        setShowContractorModal(true);

        dispatch({
            type: FETCH_USERS,
            payload: { role: 'contractor', limit: 100, page: 1 },
        });
    };

    const handleAssignToContractor = () => {
        if (!selectedContractorId) {
            toast.error('Please select a contractor');
            return;
        }
        setAssignInProgress(true);
        dispatch({
            type: ASSIGN_TO_CONTRACTOR,
            payload: {
                formId: selectedFormId,
                formName,
                level: contractorLevel, // 'l1' | 'l2' | 'root'
                contractorId: selectedContractorId,
                stationId,
            },
            cb: (res) => {
                setAssignInProgress(false);
                if (res?.status === 200) {
                    toast.success('Assigned to contractor');
                    setShowContractorModal(false);
                    fetchStationWiseForms();
                } else {
                    toast.error(res?.data?.message || 'Failed to assign');
                }
            },
        });
    };
    const openManagerModal = (form, level /* 'l1' | 'l2' | 'root' */) => {
        setSelectedFormId(form._id);
        setFormName(form.formName);
        setManagerLevel(level);
        setSelectedManagerId('');
        setShowManagerModal(true);

        dispatch({
            type: FETCH_USERS,
            payload: { role: 'manager', limit: 100, page: 1 },
        });
    };

    const handleAssignToManager = () => {
        if (!selectedManagerId) {
            toast.error('Please select a manager');
            return;
        }
        setManagerAssignInProgress(true);
        dispatch({
            type: ASSIGN_TO_MANAGER,
            payload: {
                formId: selectedFormId,
                formName,
                level: managerLevel,
                managerId: selectedManagerId,
                stationId,
            },
            cb: (res) => {
                setManagerAssignInProgress(false);
                if (res?.status === 200) {
                    toast.success('Assigned to manager');
                    setShowManagerModal(false);
                    fetchStationWiseForms();
                } else {
                    toast.error(res?.data?.message || 'Failed to assign');
                }
            },
        });
    };



    const userRole = cookie.get('user-role');

    const formNameToComponent = {
        db_panel: 'DBPanelForm',
        apfc_panel: 'APFCPanelForm',
        cabel_tray: 'CabelTrayForm',
        earthing: 'EarthingForm',
        ht_cable: 'HTCableForm',
        lightning_arrester: 'LightningArresterForm',
        lt_cable: 'LTCableForm',
        lt_panel: 'LTPanelForm',
        transformer: 'TransformerForm',
        vcb: 'VCBForm',
        hoto: 'HOTOForm',
        canopy: 'CanopyForm',
    };

    const checklistItems = [
        'APFC Panel',
        'Cabel Tray',
        'DB Panel',
        'Earthing',
        'HT Cabel',
        'Lightning Arrester',
        'LT Cabel',
        'LT Panel',
        'Transformer',
        'VCB',
        'HOTO (Project Handover Takeover Report)',
        'Canopy',
    ];

    const formNames = Object.keys(formNameToComponent);

    const prettifyFormName = (name = '') =>
        String(name).replace(/\(.*?\)/g, '').replace(/_/g, ' ').trim().toUpperCase();

    const statusLabel = (status = '', form = null, level = null) => {
        const s = (status || '').toLowerCase();
        switch (s) {
            case 'approved':
                return 'APPROVED';
            case 'rejected':
                return 'REJECTED';
            case 're-work':
            case 'rework':
                return 'RE-WORK';
            case 'submitted':
                return 'SUBMITTED';
            case 'manager_approved':
                return 'MANAGER APPROVED';
            case 'draft':
                return 'DRAFT';
            case 'contractor':
                if (level && form && form[level]?.managerId) return 'PENDING: CONT. & MGR.';
                if (!level && form && form.managerId) return 'PENDING: CONT. & MGR.';
                return 'PENDING FROM CONTRACTOR';
            case 'quality':
                return 'PENDING FROM QUALITY';
            case 'manager':
            case 'pending_manager':
            case 'pending from manager':
            case 'pending_from_manager':
                return 'PENDING FROM MANAGER';
            case 'pending':
                if (level === 'l1') return 'PENDING FROM MANAGER';
                if (level === 'l2') return 'PENDING FROM QUALITY';
                // Global inference
                if (form) {
                    if (form.l2 && (form.l2.status || '').toLowerCase() === 'pending') return 'PENDING FROM QUALITY';
                    if (form.l1 && (form.l1.status || '').toLowerCase() === 'pending') return 'PENDING FROM MANAGER';
                    if (form.managerId && (form.status || '').toLowerCase() === 'pending_manager') return 'PENDING FROM MANAGER';
                    if ((form.l1?.status || '').toLowerCase() === 'contractor' || (form.l2?.status || '').toLowerCase() === 'contractor') return 'PENDING FROM CONTRACTOR';
                }
                return 'PENDING';
            default:
                return (status || 'N/A').toUpperCase();
        }
    };


    const fetchStationWiseForms = () => {
        if (stationId) {
            dispatch({ type: FETCH_STATION_WISE_FORMS, payload: stationId });
        }
    };

    useEffect(() => {
        fetchStationWiseForms();
        setQuantities(checklistItems.reduce((acc, item) => ({ ...acc, [item]: 0 }), {}));
    }, [dispatch, stationId, statusLoading]); // keep your existing deps

    const initiateStatusChange = (formId, formName, newStatus) => {
        setSelectedFormId(formId);
        setSelectedStatus(newStatus);
        setFormName(formName);
        setShowStatusModal(true);
    };

    const handleStatusChange = () => {
        if (selectedFormId) {
            setStatusChangeInProgress(selectedFormId);
            dispatch({
                type: STATUS_CHANGE,
                payload: { formId: selectedFormId, status: selectedStatus, formName },
            });
            if (statusLoading) {
                setStatusChangeInProgress(null);
                toast.success(`Form status changed to ${selectedStatus}`);
                setShowStatusModal(false);
            }
        }
    };

    const initiateDeleteForm = (formId, formName) => {
        setSelectedFormId(formId);
        setFormName(formName);
        setShowDeleteModal(true);
    };

    const handleDeleteForm = () => {
        if (selectedFormId) {
            setDeleteInProgress(selectedFormId);
            dispatch({
                type: DELETE_CHECKLIST_FORM,
                payload: { formId: selectedFormId, formName },
            });
            if (!deleteLoading) {
                fetchStationWiseForms({});
                setShowDeleteModal(false);
            }
        }
    };

    const handleCloseStatusModal = () => {
        setShowStatusModal(false);
        setSelectedFormId(null);
        setSelectedStatus('');
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setSelectedFormId(null);
        setFormName('');
    };

    const handleAddFormsModalOpen = () => {
        setShowAddFormsModal(true);
        setAddFormError('');
    };

    const handleAddFormsModalClose = () => {
        setShowAddFormsModal(false);
        setQuantities(checklistItems.reduce((acc, item) => ({ ...acc, [item]: 0 }), {}));
        setAddFormError('');
    };

    const handleQuantityChange = (item, value) => {
        const qty = parseInt(value, 10) || 0;
        setQuantities((prev) => ({ ...prev, [item]: qty }));
        if (addFormError) setAddFormError('');
    };

    const adjustQuantity = (item, delta) => {
        setQuantities((prev) => {
            const updated = Math.max(0, (prev[item] || 0) + delta);
            return { ...prev, [item]: updated };
        });
    };

    const validateAddForms = () => {
        const totalSelected = Object.values(quantities).reduce((sum, qty) => sum + (qty > 0 ? 1 : 0), 0);
        if (totalSelected === 0) {
            setAddFormError('Select at least one form with quantity greater than 0.');
            return false;
        }
        return true;
    };

    const handleAddFormsSubmit = () => {
        if (!validateAddForms()) return;

        const checklist = {};
        Object.entries(quantities).forEach(([item, quantity]) => {
            if (quantity > 0) {
                const key = item
                    .toLowerCase()
                    .replace(/\(.*?\)/g, '')
                    .replace(/[^a-z0-9]+/g, '_')
                    .replace(/^_|_$/g, '');
                checklist[key] = quantity;
            }
        });

        const payload = {
            stationId: stationId.toUpperCase(),
            project_name: stationwiseformslist[0]?.project_name || 'Unknown',
            checklist,
            is_existing_station: true,
        };

        dispatch({
            type: CREATE_CHECKLIST,
            payload,
            cb: (res) => {
                if (res.status === 200) {
                    toast.success('Forms added successfully');
                    fetchStationWiseForms();
                    handleAddFormsModalClose();
                } else {
                    setAddFormError(res.data?.message || 'Failed to add forms');
                }
            },
        });
    };

    useEffect(() => {
        if (!downloadLoading) {
            setDownloadInProgress(null);
        }
    }, [downloadLoading]);

    const filteredForms = Array.isArray(stationwiseformslist)
        ? stationwiseformslist.filter((f) => {
            if (!f || typeof f !== 'object') return false;

            // For quality role, only show forms in quality status
            if (userRole === 'quality') {
                const s = (f.status || '').toLowerCase();
                const s1 = (f.l1?.status || '').toLowerCase();
                const s2 = (f.l2?.status || '').toLowerCase();
                if (s !== 'quality' && s1 !== 'quality' && s2 !== 'quality') return false;
            }

            const formNameMatch =
                f.formName?.toLowerCase().includes(search.toLowerCase()) ||
                f.userName?.toLowerCase().includes(search.toLowerCase());

            const formFilterMatch = !formFilter || f.formName?.toLowerCase() === formFilter.toLowerCase();

            const statusMatch = !statusFilter || (f.status || '').toLowerCase() === statusFilter.toLowerCase();

            const userMatch = !userFilter || f.userName === userFilter;

            const formDate = new Date(f.createdAt);
            const start = startDate ? new Date(startDate) : null;
            const end = endDate ? new Date(endDate) : null;

            const dateInRange =
                (!start || formDate >= start) && (!end || formDate <= new Date(end.setHours(23, 59, 59, 999)));

            return formNameMatch && formFilterMatch && statusMatch && userMatch && dateInRange;
        })
        : [];

    const sortedForms = [...filteredForms].sort((a, b) => {
        const aTime = a?.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const bTime = b?.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return bTime - aTime;
    });

    const uniqueUsers = Array.from(new Set(stationwiseformslist.map(f => f.userName).filter(Boolean))).sort();

    const handleDownload = (formId, formName, userName) => {
        if (formId) {
            setDownloadInProgress(formId);
            dispatch({
                type: DOWNLOAD_CHECKLIST_FORM,
                payload: { formId, formName, userName, stationId },
                meta: { formId },
            });
        }
    };

    const formatToIST = (utcDate) => {
        if (!utcDate) return 'N/A';
        const date = new Date(utcDate);
        return date.toLocaleString('en-IN', {
            timeZone: 'Asia/Kolkata',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        });
    };




    return (
        <div className="page-body">
            <div className="container-xl premium-dashboard-stats">
                {/* Breadcrumbs */}
                <Breadcrumb className="mb-4">
                    <Breadcrumb.Item href="#" onClick={() => navigate('/formList')}>All Sites</Breadcrumb.Item>
                    <Breadcrumb.Item active>Station Hub: {stationId}</Breadcrumb.Item>
                </Breadcrumb>

                {/* Station Hub Header */}
                <div className="station-hub-header shadow-sm">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-4">
                        <div className="station-meta">
                            <span className="badge bg-primary-lt mb-2">Station Context</span>
                            <h1 className="d-flex align-items-center gap-3">
                                <IconBuildingStore size={40} className="text-primary" />
                                {stationwiseformslist?.[0]?.project_name || 'PROJECT NAME'}
                            </h1>
                            <div className="d-flex align-items-center gap-4 mt-1 text-muted">
                                <div className="d-flex align-items-center gap-1">
                                    <IconSubtask size={18} />
                                    <span>Station ID: <strong>{stationId}</strong></span>
                                </div>
                                <div className="d-flex align-items-center gap-1">
                                    <IconLayoutGrid size={18} />
                                    <span>Total Submissions: <strong>{stationwiseformslist.length}</strong></span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-3">
                            {(userRole === 'admin' || userRole === 'engineer' || userRole === 'manager') && (
                                <Button 
                                    className="px-4 py-2 border-0 shadow-sm d-flex align-items-center gap-2"
                                    style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', fontWeight: '700' }}
                                    onClick={handleAddFormsModalOpen}
                                >
                                    <IconPlus size={20} /> Add Checklist Forms
                                </Button>
                            )}
                            <Button variant="ghost" className="text-muted border-0" onClick={() => navigate('/formList')}>
                                <IconArrowLeft size={18} className="me-2" /> Back
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Premium Toolbar: Filters & Search */}
                <div className="premium-toolbar shadow-sm">
                    <div className="d-flex flex-wrap align-items-center gap-3 flex-grow-1">
                        <div className={`search-container ${isSearchExpanded ? 'expanded' : ''}`} style={{ maxWidth: '300px' }}>
                            <InputGroup className="premium-input-group shadow-sm" style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                <InputGroup.Text className="bg-transparent border-0 pe-0">
                                    <IconSearch size={18} className="text-muted" />
                                </InputGroup.Text>
                                <Form.Control
                                    className="border-0 bg-transparent py-2"
                                    placeholder="Search by ID or Filer..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onFocus={() => setIsSearchExpanded(true)}
                                    onBlur={() => !search && setIsSearchExpanded(false)}
                                    autoComplete="off"
                                />
                            </InputGroup>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <IconFilter size={18} className="text-muted" />
                            <Form.Select 
                                className="premium-select shadow-sm" 
                                style={{ borderRadius: '12px', border: '1px solid #e2e8f0', width: '180px' }}
                                value={formFilter}
                                onChange={(e) => setFormFilter(e.target.value)}
                            >
                                <option value="">All Form Types</option>
                                {formNames.map(f => (
                                    <option key={f} value={f}>{prettifyFormName(f)}</option>
                                ))}
                            </Form.Select>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <Form.Select 
                                className="premium-select shadow-sm"
                                style={{ borderRadius: '12px', border: '1px solid #e2e8f0', width: '160px' }}
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="">All Statuses</option>
                                <option value="Approved">Approved</option>
                                <option value="Pending">Pending</option>
                                <option value="Contractor">Contractor</option>
                                <option value="Manager">Manager</option>
                                <option value="Quality">Quality</option>
                                <option value="Rejected">Rejected</option>
                                <option value="Draft">Draft</option>
                            </Form.Select>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <Form.Select 
                                className="premium-select shadow-sm"
                                style={{ borderRadius: '12px', border: '1px solid #e2e8f0', width: '160px' }}
                                value={userFilter}
                                onChange={(e) => setUserFilter(e.target.value)}
                            >
                                <option value="">All Filers</option>
                                {uniqueUsers.map(u => (
                                    <option key={u} value={u}>{u}</option>
                                ))}
                            </Form.Select>
                        </div>

                        <div className="d-flex align-items-center gap-3">
                            <div className="d-flex align-items-center gap-2">
                                <IconCalendar size={18} className="text-muted" />
                                <Form.Control 
                                    type="date" 
                                    className="premium-date shadow-sm"
                                    style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                />
                                <span className="text-muted">to</span>
                                <Form.Control 
                                    type="date" 
                                    className="premium-date shadow-sm"
                                    style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                    
                    <Button variant="ghost" className="text-danger" onClick={clearFilters}>
                        Clear Filters
                    </Button>
                </div>

                {/* Forms Table */}
                {loading ? (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5">
                       <IconLoader2 size={48} className="animate-spin text-primary mb-3" />
                       <p className="text-muted fw-bold">Loading station forms...</p>
                    </div>
                ) : error ? (
                    <Alert variant="danger" className="glass-panel text-center border-0 py-5 shadow-sm">
                        <IconX size={48} className="mb-3 text-danger" />
                        <h4 className="fw-bold text-danger">Error Loading Data</h4>
                        <p className="text-muted mb-0">{error.message || 'Failed to fetch forms. Please try again.'}</p>
                    </Alert>
                ) : (
                    <div className="glass-panel shadow-sm" style={{ overflow: 'hidden' }}>
                        <div className="table-responsive">
                            <table className="table table-vcenter table-mobile-md card-table">
                                <thead style={{ background: '#f8fafc' }}>
                                    <tr>
                                        <th className="px-4 py-3 text-muted fw-bold text-uppercase small" style={{ fontSize: '11px' }}>Form Identity</th>
                                        <th className="py-3 text-muted fw-bold text-uppercase small" style={{ fontSize: '11px' }}>Global Status</th>
                                        <th className="py-3 text-muted fw-bold text-uppercase small" style={{ fontSize: '11px' }}>L1 Workflow</th>
                                        <th className="py-3 text-muted fw-bold text-uppercase small" style={{ fontSize: '11px' }}>L2 Workflow</th>
                                        <th className="py-3 text-muted fw-bold text-uppercase small" style={{ fontSize: '11px' }}>Filer</th>
                                        <th className="py-3 text-muted fw-bold text-uppercase small" style={{ fontSize: '11px' }}>Updated</th>
                                        <th className="px-4 py-3 text-end text-muted fw-bold text-uppercase small w-1" style={{ fontSize: '11px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedForms.length > 0 ? (
                                        sortedForms.map((form) => (
                                            <tr key={form._id}>
                                                <td className="px-4">
                                                    <div className="d-flex align-items-center">
                                                        <div className="avatar me-3 bg-blue-lt flex-shrink-0" style={{ width: '36px', height: '36px' }}>
                                                            <IconLayoutGrid size={18} />
                                                        </div>
                                                        <div>
                                                            <div className="fw-bold" style={{ color: '#1e293b', fontSize: '14px' }}>{prettifyFormName(form.formName)}</div>
                                                            <div className="text-muted small fw-mono" style={{ fontSize: '11px' }}>ID: #{form._id.slice(-6).toUpperCase()}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    {form.formName === 'hoto' && (form.status === 'MANAGER_APPROVED' || (form.status || '').toLowerCase() === 'approved') ? (
                                                        (form.status || '').toLowerCase() === 'approved' ? (
                                                            <span className="badge bg-success-lt font-weight-bold py-1 px-2">APPROVED</span>
                                                        ) : (
                                                            <div className="d-flex flex-column gap-1 align-items-start">
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <span className="text-muted fw-bold" style={{fontSize: '9px', minWidth: '12px'}}>M:</span>
                                                                    <span className={`badge bg-${(form.maintenance_status || 'pending') === 'approved' ? 'success' : 'warning'}-lt py-0 px-1`} style={{fontSize: '10px'}}>
                                                                        {(form.maintenance_status || 'pending').toUpperCase()}
                                                                    </span>
                                                                </div>
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <span className="text-muted fw-bold" style={{fontSize: '9px', minWidth: '12px'}}>O:</span>
                                                                    <span className={`badge bg-${(form.operations_status || 'pending') === 'approved' ? 'success' : 'warning'}-lt py-0 px-1`} style={{fontSize: '10px'}}>
                                                                        {(form.operations_status || 'pending').toUpperCase()}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )
                                                    ) : (
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span className={`badge ${
                                                                (form.status || '').toLowerCase() === 'approved' ? 'bg-success-lt font-weight-bold' :
                                                                (form.status || '').toLowerCase().includes('pending') ? 'bg-warning-lt' :
                                                                (form.status || '').toLowerCase().includes('contractor') ? 'bg-orange-lt' :
                                                                (form.status || '').toLowerCase().includes('quality') ? 'bg-purple-lt' :
                                                                'bg-secondary-lt'
                                                            } py-1 px-2`}>
                                                                {statusLabel(form.status, form)}
                                                            </span>
                                                            {!form.l1 && !form.l2 && form.formName !== 'hoto' && userRole === 'engineer' && (
                                                                <div className="d-flex align-items-center gap-2">
                                                                    {(form.status || '').toLowerCase() === 'contractor' && (
                                                                        <button 
                                                                            className="btn-link p-0 border-0 bg-transparent text-primary d-flex" 
                                                                            title={form.contractorId ? "View Contractor" : "Assign Contractor"}
                                                                            onClick={() => openContractorModal(form, 'root')}
                                                                        >
                                                                            {form.contractorId ? <IconInfoCircle size={14} /> : <IconUserPlus size={14} />}
                                                                        </button>
                                                                    )}
                                                                    {(form.status || '').toLowerCase().includes('manager') && (
                                                                        <button 
                                                                            className="btn-link p-0 border-0 bg-transparent text-success d-flex" 
                                                                            title={form.managerId ? "View Manager" : "Assign Manager"}
                                                                            onClick={() => openManagerModal(form, 'root')}
                                                                        >
                                                                            {form.managerId ? <IconInfoCircle size={14} /> : <IconShieldCheck size={14} />}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    {(form.l1 || form.formName === 'hoto') ? (
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span className={`badge bg-${( (form.l1?.status || form.status) || '').toLowerCase() === 'approved' ? 'success' : 'azure'}-lt`}>
                                                                {form.formName === 'hoto' ? statusLabel(form.status, form) : statusLabel(form.l1?.status, form, 'l1')}
                                                            </span>
                                                            {/* HOTO Manager Assignment */}
                                                            {form.formName === 'hoto' && userRole === 'engineer' && (form.status === 'SUBMITTED' || (form.status || '').toLowerCase().includes('manager')) && (
                                                                <button 
                                                                    className="btn-link p-0 border-0 bg-transparent text-success d-flex" 
                                                                    title={form.managerId ? "View Manager" : "Assign Manager"}
                                                                    onClick={() => openManagerModal(form, 'root')}
                                                                >
                                                                    {form.managerId ? <IconInfoCircle size={14} /> : <IconShieldCheck size={14} />}
                                                                </button>
                                                            )}
                                                            {/* Standard L1/L2 logic */}
                                                            {form.l1 && userRole === 'engineer' && (form.l1.status || '').toLowerCase() === 'contractor' && (
                                                                <button 
                                                                    className="btn-link p-0 border-0 bg-transparent text-primary d-flex" 
                                                                    title={form.l1.contractorId ? "View Contractor" : "Assign Contractor"}
                                                                    onClick={() => openContractorModal(form, 'l1')}
                                                                >
                                                                    {form.l1.contractorId ? <IconInfoCircle size={14} /> : <IconUserPlus size={14} />}
                                                                </button>
                                                            )}
                                                            {form.l1 && userRole === 'engineer' && (form.l1.status || '').toLowerCase().includes('manager') && (
                                                                <button 
                                                                    className="btn-link p-0 border-0 bg-transparent text-success d-flex" 
                                                                    title={form.l1.managerId ? "View Manager" : "Assign Manager"}
                                                                    onClick={() => openManagerModal(form, 'l1')}
                                                                >
                                                                    {form.l1.managerId ? <IconInfoCircle size={14} /> : <IconShieldCheck size={14} />}
                                                                </button>
                                                            )}
                                                        </div>
                                                    ) : <span className="text-muted small">—</span>}
                                                </td>
                                                <td>
                                                    {form.l2 ? (
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span className={`badge bg-${(form.l2.status || '').toLowerCase() === 'approved' ? 'success' : 'purple'}-lt`}>
                                                                {statusLabel(form.l2.status, form, 'l2')}
                                                            </span>
                                                            {userRole === 'engineer' && (form.l2.status || '').toLowerCase() === 'contractor' && (
                                                                <button 
                                                                    className="btn-link p-0 border-0 bg-transparent text-primary d-flex" 
                                                                    title={form.l2.contractorId ? "View Contractor" : "Assign Contractor"}
                                                                    onClick={() => openContractorModal(form, 'l2')}
                                                                >
                                                                    {form.l2.contractorId ? <IconInfoCircle size={14} /> : <IconUserPlus size={14} />}
                                                                </button>
                                                            )}
                                                            {userRole === 'engineer' && (form.l2.status || '').toLowerCase().includes('manager') && (
                                                                <button 
                                                                    className="btn-link p-0 border-0 bg-transparent text-success d-flex" 
                                                                    title={form.l2.managerId ? "View Manager" : "Assign Manager"}
                                                                    onClick={() => openManagerModal(form, 'l2')}
                                                                >
                                                                    {form.l2.managerId ? <IconInfoCircle size={14} /> : <IconShieldCheck size={14} />}
                                                                </button>
                                                            )}

                                                        </div>
                                                    ) : <span className="text-muted small">—</span>}
                                                </td>

                                                <td>
                                                    <div className="text-muted small">{form.userName || 'N/A'}</div>
                                                </td>

                                                <td>
                                                    <div className="small text-muted">{formatToIST(form?.updatedAt).split(',')[0]}</div>
                                                    <div className="small text-muted" style={{ fontSize: '10px' }}>Last Touch</div>
                                                </td>
                                                <td className="px-4">
                                                    <div className="d-flex justify-content-end gap-1">
                                                        {userRole === 'manager' && (form.status === 'MANAGER' || form.status === 'PENDING_MANAGER' || form.status === 'pending') && form.managerId === cookie.get('user-id') && (
                                                            <>
                                                                <Button 
                                                                    variant="light" 
                                                                    size="sm" 
                                                                    className="p-1 border-0 bg-transparent text-success"
                                                                    onClick={() => initiateStatusChange(form._id, form.formName, 'approved')}
                                                                >
                                                                    <IconCheck size={18} />
                                                                </Button>
                                                                <Button 
                                                                    variant="light" 
                                                                    size="sm" 
                                                                    className="p-1 border-0 bg-transparent text-danger"
                                                                    onClick={() => initiateStatusChange(form._id, form.formName, 'rejected')}
                                                                >
                                                                    <IconX size={18} />
                                                                </Button>
                                                            </>
                                                        )}
                                                        
                                                        <Button 
                                                            variant="light" 
                                                            size="sm" 
                                                            className="p-1 border-0 bg-transparent text-primary"
                                                            title="Download PDF"
                                                            onClick={() => handleDownload(form._id, form?.formName, form?.userName)}
                                                            disabled={downloadInProgress === form._id}
                                                        >
                                                            {downloadInProgress === form._id ? <IconLoader2 className="animate-spin" size={18} /> : <IconDownload size={18} />}
                                                        </Button>

                                                        <Button 
                                                            variant="light" 
                                                            size="sm" 
                                                            className="p-1 border-0 bg-transparent text-indigo"
                                                            title="Edit Form"
                                                            onClick={() => navigate(`/formList/${stationId}/${form?.formName}/${form._id}`)}
                                                            disabled={!form._id}
                                                        >
                                                            <IconEdit size={18} />
                                                        </Button>



                                                        {userRole === 'engineer' && (
                                                            <Button 
                                                                variant="light" 
                                                                size="sm" 
                                                                className="p-1 border-0 bg-transparent text-danger"
                                                                title="Delete Form"
                                                                onClick={() => initiateDeleteForm(form._id, form.formName)}
                                                                disabled={deleteInProgress === form._id}
                                                            >
                                                                <IconTrash size={18} />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="text-center py-5 text-muted border-0">
                                                <div className="py-4">
                                                    <IconBox size={48} className="mb-3 opacity-20" />
                                                    <p className="fw-bold mb-0">No forms found for this station</p>
                                                    <p className="small text-muted">Initialize a new checklist or change filters</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Status Change Confirmation Modal */}
                <Modal show={showStatusModal} onHide={handleCloseStatusModal} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Status Change</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>Are you sure you want to {statusLabel(selectedStatus)} this form?</Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseStatusModal}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleStatusChange}
                            disabled={statusChangeInProgress === selectedFormId}
                        >
                            {statusChangeInProgress === selectedFormId ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                    Changing...
                                </>
                            ) : (
                                'Confirm'
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Delete</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>{`Are you sure you want to delete the form "${formName}"?`}</Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseDeleteModal}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={handleDeleteForm} disabled={deleteInProgress === selectedFormId}>
                            {deleteInProgress === selectedFormId ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                    Deleting...
                                </>
                            ) : (
                                'Delete'
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* ✅ Send to Contractor Modal (RESTORING) */}
                <Modal show={showContractorModal} onHide={() => setShowContractorModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Contractor Assignment</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="mb-2">
                            <strong>Form:</strong> {prettifyFormName(formName)} &nbsp;|&nbsp; <strong>Level:</strong>{' '}
                            {contractorLevel === 'root' ? 'ROOT' : String(contractorLevel || '').toUpperCase()}
                        </div>
                        {(() => {
                            // Find the current form
                            const currentForm = stationwiseformslist.find(f => f._id === selectedFormId);
                            if (!currentForm) return <div>Form not found</div>;

                            const levelData = contractorLevel === 'root' ? currentForm : currentForm[contractorLevel];
                            const isAssigned = levelData?.contractorId;
                            const assignedContractor = isAssigned ? contractors.find(c => (c.id || c._id) === levelData.contractorId) : null;
                            const assignedAt = levelData?.contractorAssignedAt ? formatToIST(levelData.contractorAssignedAt) : null;

                            if (isAssigned && assignedContractor) {
                                return (
                                    <div className="premium-card p-3 border-0" style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
                                        <h5 className="mb-3" style={{ color: '#1abc9c' }}>Assigned Contractor</h5>
                                        <div className="d-flex align-items-center mb-3">
                                            <div className="avatar me-3 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                                {assignedContractor.name?.charAt(0) || 'C'}
                                            </div>
                                            <div>
                                                <div className="fw-bold">{assignedContractor.name || `${assignedContractor.firstName || ''} ${assignedContractor.lastName || ''}`.trim()}</div>
                                                <div className="small text-muted">{assignedContractor.email}</div>
                                            </div>
                                        </div>
                                        {assignedAt && (
                                            <div className="small border-top pt-2 mt-2">
                                                <span className="text-muted">Assigned On:</span> <span className="fw-bold">{assignedAt}</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            } else {
                                return (
                                    <Form.Group>
                                        <Form.Label>Select Contractor</Form.Label>
                                        <Form.Select
                                            value={selectedContractorId}
                                            onChange={(e) => setSelectedContractorId(e.target.value)}
                                        >
                                            <option value="">Choose a contractor</option>
                                            {contractors.map((u) => (
                                                <option key={u.id || u._id} value={u.id || u._id}>
                                                    {u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim()} ({u.email})
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Text className="text-muted small mt-2 d-block">
                                            Only users with the <strong>Contractor</strong> role are listed here.
                                        </Form.Text>
                                    </Form.Group>
                                );
                            }
                        })()}
                    </Modal.Body>
                    <Modal.Footer>
                        {(() => {
                            const currentForm = stationwiseformslist.find(f => f._id === selectedFormId);
                            const levelData = contractorLevel === 'root' ? currentForm : currentForm?.[contractorLevel];
                            const isAssigned = levelData?.contractorId;

                            if (!isAssigned) {
                                return (
                                    <>
                                        <Button variant="secondary" onClick={() => setShowContractorModal(false)}>
                                            Cancel
                                        </Button>
                                        <Button 
                                            variant="primary" 
                                            onClick={handleAssignToContractor} 
                                            disabled={assignInProgress || !selectedContractorId}
                                            style={{ background: '#1abc9c', border: 'none' }}
                                        >
                                            {assignInProgress ? (
                                                <><IconLoader2 className="animate-spin me-2" /> Sending...</>
                                            ) : (
                                                'Send Assignment'
                                            )}
                                        </Button>
                                    </>
                                );
                            } else {
                                return (
                                    <Button variant="secondary" onClick={() => setShowContractorModal(false)}>
                                        Close
                                    </Button>
                                );
                            }
                        })()}
                    </Modal.Footer>
                </Modal>

                {/* ✅ Assign to Manager Modal */}
                <Modal show={showManagerModal} onHide={() => setShowManagerModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Manager Assignment</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="mb-2">
                            <strong>Form:</strong> {prettifyFormName(formName)} &nbsp;|&nbsp; <strong>Level:</strong>{' '}
                            {managerLevel === 'root' ? 'ROOT' : String(managerLevel || '').toUpperCase()}
                        </div>
                        {(() => {
                            const currentForm = stationwiseformslist.find(f => f._id === selectedFormId);
                            if (!currentForm) return <div>Form not found</div>;

                            const levelData = managerLevel === 'root' ? currentForm : currentForm[managerLevel];
                            const isAssigned = levelData?.managerId;
                            const assignedManager = isAssigned ? managers.find(m => (m.id || m._id) === levelData.managerId) : null;
                            const assignedAt = levelData?.managerAssignedAt ? formatToIST(levelData.managerAssignedAt) : null;

                            if (isAssigned && assignedManager) {
                                return (
                                    <div className="premium-card p-3 border-0" style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
                                        <h5 className="mb-3" style={{ color: '#1abc9c' }}>Assigned Manager</h5>
                                        <div className="d-flex align-items-center mb-3">
                                            <div className="avatar me-3 bg-success text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                                {assignedManager.name?.charAt(0) || 'M'}
                                            </div>
                                            <div>
                                                <div className="fw-bold">{assignedManager.name || `${assignedManager.firstName || ''} ${assignedManager.lastName || ''}`.trim()}</div>
                                                <div className="small text-muted">{assignedManager.email}</div>
                                            </div>
                                        </div>
                                        {assignedAt && (
                                            <div className="small border-top pt-2 mt-2">
                                                <span className="text-muted">Assigned On:</span> <span className="fw-bold">{assignedAt}</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            } else {
                                return (
                                    <Form.Group>
                                        <Form.Label>Select Manager</Form.Label>
                                        <Form.Select
                                            value={selectedManagerId}
                                            onChange={(e) => setSelectedManagerId(e.target.value)}
                                        >
                                            <option value="">Choose a manager</option>
                                            {managers.map((u) => (
                                                <option key={u.id || u._id} value={u.id || u._id}>
                                                    {u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim()} ({u.email})
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Text className="text-muted small mt-2 d-block">
                                            Only users with the <strong>Manager</strong> role are listed here.
                                          </Form.Text>
                                    </Form.Group>
                                );
                            }
                        })()}
                    </Modal.Body>
                    <Modal.Footer>
                        {(() => {
                            const currentForm = stationwiseformslist.find(f => f._id === selectedFormId);
                            const levelData = managerLevel === 'root' ? currentForm : currentForm?.[managerLevel];
                            const isAssigned = levelData?.managerId;

                            if (!isAssigned) {
                                return (
                                    <>
                                        <Button variant="secondary" onClick={() => setShowManagerModal(false)}>
                                            Cancel
                                        </Button>
                                        <Button 
                                            variant="primary" 
                                            onClick={handleAssignToManager} 
                                            disabled={managerAssignInProgress || !selectedManagerId}
                                            style={{ background: '#1abc9c', border: 'none' }}
                                        >
                                            {managerAssignInProgress ? (
                                                <><IconLoader2 className="animate-spin me-2" /> Assigning...</>
                                            ) : (
                                                'Assign Manager'
                                            )}
                                        </Button>
                                    </>
                                );
                            } else {
                                return (
                                    <Button variant="secondary" onClick={() => setShowManagerModal(false)}>
                                        Close
                                      </Button>
                                );
                            }
                        })()}
                    </Modal.Footer>
                </Modal>



                {/* Add Forms Modal (unchanged) */}
                <Modal show={showAddFormsModal} onHide={handleAddFormsModalClose} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Add Forms to Station: {stationId}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {addFormError && (
                            <Alert variant="danger" className="mb-3">
                                {addFormError}
                            </Alert>
                        )}
                        <ListGroup>
                            {checklistItems.map((item) => (
                                <ListGroup.Item key={item} className="d-flex align-items-center">
                                    <span className="flex-grow-1">{item}</span>
                                    <InputGroup style={{ width: '150px' }}>
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => adjustQuantity(item, -1)}
                                            disabled={quantities[item] === 0}
                                        >
                                            −
                                        </Button>
                                        <Form.Control
                                            type="number"
                                            value={quantities[item]}
                                            min={0}
                                            onChange={(e) => handleQuantityChange(item, e.target.value)}
                                            style={{ textAlign: 'center' }}
                                        />
                                        <Button variant="outline-secondary" onClick={() => adjustQuantity(item, 1)}>
                                            +
                                        </Button>
                                    </InputGroup>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleAddFormsModalClose}>
                            Cancel
                        </Button>
                        <Button variant="success" onClick={handleAddFormsSubmit}>
                            Send
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
};

export default StationForms;
